import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ChefHat, Sparkles, RefreshCw, AlertCircle, UtensilsCrossed } from 'lucide-react';

const DailyMenuSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [menuData, setMenuData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const sectionRef = useRef(null);
  const { language } = useLanguage();


  const content = {
    it: {
      subtitle: "Ogni giorno qualcosa di speciale",
      title: "Menu del Giorno",
      chefRecommends: "Cosa consiglia lo chef oggi",
      loading: "Caricamento menu...",
      closed: "Oggi siamo chiusi!",
      noMenu: "Oggi il menu non è disponibile.",
      noDailyMenu: "Oggi non è presente il menu del giorno",
      alaCarte: "Vi proponiamo il nostro menu à la carte",
      viewMenu: "Scopri il Menu",
      error: "Errore nel caricamento del menu",
      retry: "Riprova",
      note: "Menu aggiornato quotidianamente",
      availableDays: "Menu del giorno disponibile giovedì e venerdì a pranzo"
    },
    de: {
      subtitle: "Jeden Tag etwas Besonderes",
      title: "Tagesmenü",
      chefRecommends: "Was der Küchenchef heute empfiehlt",
      loading: "Menü wird geladen...",
      closed: "Heute sind wir geschlossen!",
      noMenu: "Das Tagesmenü ist heute nicht verfügbar.",
      noDailyMenu: "Heute gibt es kein Tagesmenü",
      alaCarte: "Wir bieten Ihnen unsere à la carte Speisekarte an",
      viewMenu: "Menü entdecken",
      error: "Fehler beim Laden des Menüs",
      retry: "Erneut versuchen",
      note: "Menü wird täglich aktualisiert",
      availableDays: "Tagesmenü verfügbar donnerstags und freitags mittags"
    },
    en: {
      subtitle: "Something special every day",
      title: "Daily Menu",
      chefRecommends: "Chef's recommendations today",
      loading: "Loading menu...",
      closed: "We're closed today!",
      noMenu: "Today's menu is not available.",
      noDailyMenu: "No daily menu today",
      alaCarte: "We offer you our à la carte menu",
      viewMenu: "View Menu",
      error: "Error loading menu",
      retry: "Retry",
      note: "Menu updated daily",
      availableDays: "Daily menu available Thursday and Friday at lunch"
    }
  };

  const t = content[language];

  const categoryLabels = {
    it: {
      primo: "Primo",
      secondo: "Secondo",
      pizza: "Pizza",
      dolce: "Dolce",
      antipasto: "Antipasto",
      contorno: "Contorno"
    },
    de: {
      primo: "Erster Gang",
      secondo: "Hauptgericht",
      pizza: "Pizza",
      dolce: "Dessert",
      antipasto: "Vorspeise",
      contorno: "Beilage"
    },
    en: {
      primo: "First Course",
      secondo: "Main Course",
      pizza: "Pizza",
      dolce: "Dessert",
      antipasto: "Appetizer",
      contorno: "Side Dish"
    }
  };

  const fetchDailyMenu = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/menu-del-giorno');
      if (!response.ok) throw new Error('Failed to fetch menu');
      const data = await response.json();
      setMenuData(data);
    } catch (err) {
      console.error('Error fetching daily menu:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDailyMenu();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchDailyMenu, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchDailyMenu]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const getCategoryLabel = (category) => {
    const cat = category.toLowerCase();
    return categoryLabels[language]?.[cat] || category;
  };

  const groupedItems = menuData?.items?.reduce((acc, item) => {
    const cat = item.categoria;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {}) || {};

  return (
    <section
      id="menu-del-giorno"
      ref={sectionRef}
      className="py-20 bg-[#1a1a1a] relative overflow-hidden"
    >
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, #c9a227 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section header */}
        <div className={`text-center mb-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-block text-[#c9a227] font-medium tracking-[0.2em] uppercase text-sm mb-4">
            {t.subtitle}
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-4">
            {t.title}
          </h2>
          <div className="w-24 h-0.5 bg-[#c9a227] mx-auto"></div>
        </div>

        {/* Daily Menu Card */}
        <div className={`max-w-3xl mx-auto transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="bg-[#252525] rounded-lg border border-[#c9a227]/30 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#c9a227]/20 to-transparent px-6 py-4 border-b border-[#c9a227]/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#c9a227]/20 flex items-center justify-center">
                    <ChefHat className="w-6 h-6 text-[#c9a227]" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl font-bold text-white">{t.chefRecommends}</h3>
                    <p className="text-sm text-white/50">{t.availableDays}</p>
                  </div>
                </div>
                {!loading && (
                  <button 
                    onClick={fetchDailyMenu}
                    className="p-2 rounded-full hover:bg-white/10 transition-colors"
                    title={t.retry}
                  >
                    <RefreshCw className="w-5 h-5 text-white/50 hover:text-[#c9a227]" />
                  </button>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-8">
              {/* Loading state */}
              {loading && (
                <div className="text-center py-8">
                  <div className="w-12 h-12 border-2 border-[#c9a227]/30 border-t-[#c9a227] rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-white/50">{t.loading}</p>
                </div>
              )}

              {/* Error state */}
              {error && !loading && (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 text-red-500/50 mx-auto mb-4" />
                  <p className="text-white/50 mb-4">{t.error}</p>
                  <button 
                    onClick={fetchDailyMenu}
                    className="px-4 py-2 bg-[#c9a227] text-[#1a1a1a] rounded-full font-semibold hover:bg-[#e8d48b] transition-colors"
                  >
                    {t.retry}
                  </button>
                </div>
              )}

              {/* Closed state (Monday) */}
              {!loading && !error && menuData?.is_closed && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-[#c9a227]/10 flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-8 h-8 text-[#c9a227]" />
                  </div>
                  <p className="text-2xl font-serif text-[#c9a227] font-bold">{t.closed}</p>
                </div>
              )}

              {/* No daily menu today (not Thursday/Friday) */}
              {!loading && !error && !menuData?.is_closed && menuData?.no_daily_menu && (
                <div className="text-center py-10">
                  <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                    <UtensilsCrossed className="w-8 h-8 text-[#c9a227]" />
                  </div>
                  <p className="text-xl font-serif text-white mb-2">{t.noDailyMenu}</p>
                  <p className="text-white/60 mb-6">{t.alaCarte}</p>
                  <a 
                    href="#menu"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#c9a227] text-[#1a1a1a] rounded-full font-semibold hover:bg-[#e8d48b] transition-colors"
                  >
                    {t.viewMenu}
                    <span>→</span>
                  </a>
                </div>
              )}

              {/* No menu items for today (Thursday/Friday but no items in sheet) */}
              {!loading && !error && !menuData?.is_closed && !menuData?.no_daily_menu && menuData?.items?.length === 0 && (
                <div className="text-center py-12">
                  <Sparkles className="w-12 h-12 text-[#c9a227]/40 mx-auto mb-4" />
                  <p className="text-white/40 text-lg italic">{menuData?.message || t.noMenu}</p>
                </div>
              )}

              {/* Menu items */}
              {!loading && !error && !menuData?.is_closed && !menuData?.no_daily_menu && menuData?.items?.length > 0 && (
                <div className="space-y-6">
                  {menuData.categories.map((category, catIndex) => (
                    <div key={category} className="animate-fadeIn" style={{ animationDelay: `${catIndex * 100}ms` }}>
                      {/* Category title */}
                      <div className="mb-3">
                        <span className="text-xs uppercase tracking-wider text-[#c9a227]/70 font-semibold">
                          {getCategoryLabel(category)}
                        </span>
                      </div>
                      
                      {/* Items in category */}
                      <div className="space-y-3">
                        {groupedItems[category]?.map((item, itemIndex) => (
                          <div 
                            key={`${item.nome_piatto}-${itemIndex}`}
                            className="bg-[#1a1a1a]/50 border border-[#c9a227]/10 rounded-lg p-4 hover:border-[#c9a227]/30 transition-all duration-300 group"
                          >
                            <div className="flex justify-between items-start gap-4">
                              <h4 className="font-semibold text-white text-lg group-hover:text-[#c9a227] transition-colors whitespace-pre-line">
                                {item.nome_piatto}
                              </h4>
                              <div className="flex-shrink-0 px-4 py-2 rounded-full bg-[#c9a227] shadow-lg">
                                <span className="text-[#1a1a1a] font-bold text-sm whitespace-nowrap">
                                  CHF {item.prezzo}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DailyMenuSection;
